import React, { useRef, useEffect } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { AtlassianIcon } from "@atlaskit/logo";
import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { ButtonColor } from "@/components/adg4/button/src";
import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework";
import { ProductKeyTypes } from "@/libraries/wpl/foundations/src";
import { useExperiment, experiments } from "@/utils/experiment";

import { useMore } from "./hooks/use-more";
import { MobileNavLinksContainerInfo } from "./mobile-nav/mobile-nav-links-container";
import {
    MobileProductPageNav,
    AdditionalMenuPageObjects
} from "./mobile-nav/mobile-product-page-nav";
import GlobalNavLinksContainer, {
    GlobalNavLinkContentProps,
    GlobalNavProvider
} from "./nav-links-container";
import SearchAndProfile from "./nav-search-and-profile";
import SearchBar from "./search-bar";
import { SkipToContent } from "./skip-to-content";

const mobileWrapperCss = css({
    display: "block",
    position: "sticky",
    insetBlockStart: "0",
    zIndex: "5",
    [media.below.md]: {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".product-page-desktop-nav": {
            display: "none"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".product-page-mobile-nav": {
            display: "block"
        }
    }
});

const productPageMobileNav = css({
    display: "none"
});

const atlasIconContainerStyle = css({
    height: "68px",
    width: "68px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: wacToken("color.extended.blue700")
});

const atlasIconStyle = css({
    height: "56px",
    width: "56px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
});

const productPageSubNavStyle = css({
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    position: "relative",
    alignItems: "center",
    backgroundColor: wacToken("color.neutral.light0"),
    flex: 1,
    zIndex: 5,
    width: "100%",
    height: "68px"
});

const productpageSubnavBar = css({
    display: "flex",
    flex: 1,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    listStyle: "none",
    padding: 0,

    [media.above.sm]: {
        maxWidth: "1152px"
    }
});

const wideProductPageSubnavBar = css({
    [media.above.sm]: {
        maxWidth: "1180px"
    }
});

const productpageSubnavLogoAndLinksContainer = css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    flex: 1
});

const productpageSubnavProductLogo = css({
    // This is admittedly a magic number that accounts for the tendency for our logo + wordmark lockup having descenders below the baseline
    paddingBlockStart: "3px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
});

const aNavBarLogoLink = css({
    display: "flex",
    justifyContent: "center",
    alignItems: "end",
    height: "26px",

    // Adding the eslint disable rule above, the confluence logo could only be changed to the correct size by targeting the span or svg
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    span: {
        height: "26px"
    }
});

const productpageSearchBar = css({
    width: "100%",

    [media.above.sm]: {
        maxWidth: "1152px"
    }
});

// GALILEO-1972
// The following are used to fit the 'Templates' tab in the nav as part of this experiment:
// https://hello.atlassian.net/wiki/spaces/Growth/pages/4077753200/Templates+tab+in+WAC+header+iteration+GRW-1175
const getBuffer = (isTemplatesTabInHeader: boolean | undefined) =>
    isTemplatesTabInHeader ? 2 : undefined;

const smallerJsmLogoStyle = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    svg: {
        height: "24px"
    }
});
// GALILEO-1972

export type BasedProductPageSubNavProps = {
    readonly productLogo: JSX.Element;
    readonly productLogoUrl: string;
    readonly productLogoAriaLabel: string;
    readonly productKey?: ProductKeyTypes;
    readonly productPageNavLinks: GlobalNavLinkContentProps[];
    readonly isShowSearchBarAndProfileContainer?: boolean;
    readonly isShowTryNowLink?: boolean;
    readonly tryNowCTAButtonBackgroundColor?: ButtonColor;
    readonly isShowSearchButton?: boolean;
    readonly navigationTrynowCtaText?: string;
    readonly navigationTrynowCtaUrl?: string;
    readonly navigationTrynowCtaOnClick?: (e: React.MouseEvent) => void;
    readonly navigationTrynowCtaButtonVariant?: "primary" | "secondary";
    readonly positionTryNowCtaOnRight?: boolean;
    readonly isShowAppSwitcher?: boolean;
    readonly isShowTabletDropdown?: boolean;
    readonly isTemplatesTabInHeader?: boolean;
};

export type ProductPageSubNavProps = BasedProductPageSubNavProps & {
    readonly mobileLogo?: JSX.Element;
    readonly mobileLinksSections: MobileNavLinksContainerInfo[];
    readonly isIncludeMobileCTA?: boolean;
    readonly mobileCtaText?: string;
    readonly mobileCtaUrl?: string;
    readonly mobileCtaOnClick?: (e: React.MouseEvent) => void;
    readonly isIncludeMobileAtlassianLogo?: boolean;
    readonly additionalMenuPageObjectsArray?: AdditionalMenuPageObjects[];
};

type ProductPageSubNavSetStateProps = {
    readonly isOpenSearch: boolean;
    readonly setIsOpenSearch: React.Dispatch<React.SetStateAction<boolean>>;
};

type ProductPageSubNavLinksProps = BasedProductPageSubNavProps &
    ProductPageSubNavSetStateProps;

const ProductPageSubNavLinks = ({
    productLogo,
    productLogoUrl,
    productLogoAriaLabel,
    productKey,
    productPageNavLinks,
    isShowSearchBarAndProfileContainer = true,
    isShowTryNowLink = true,
    tryNowCTAButtonBackgroundColor,
    isShowSearchButton = true,
    isOpenSearch,
    setIsOpenSearch,
    navigationTrynowCtaText = "Get it free",
    navigationTrynowCtaUrl = "/try",
    navigationTrynowCtaOnClick,
    navigationTrynowCtaButtonVariant = "primary",
    positionTryNowCtaOnRight = false,
    isShowAppSwitcher = true,
    isTemplatesTabInHeader
}: // eslint-disable-next-line complexity
ProductPageSubNavLinksProps) => {
    const linksRef = useRef<HTMLUListElement | null>(null);
    const searchAndProfileRef = useRef<HTMLDivElement | null>(null);
    const hasMounted = useRef(false);

    const { showMoreItems, shouldShowMoreItems, maxItems } = useMore({
        leftElementRef: linksRef,
        rightElementRef: searchAndProfileRef,
        initialMaxItems: productPageNavLinks.length,
        buffer: getBuffer(isTemplatesTabInHeader)
    });

    // Focus the search button when the search bar is closed
    useEffect(() => {
        if (hasMounted.current) {
            const searchButton = document.getElementById(
                "global-nav-search-bar-icon-desktop"
            );
            if (searchButton) {
                searchButton.focus();
            }
        } else {
            hasMounted.current = true;
        }
    }, [isOpenSearch]);

    const wacAISearchSummaryExperiment = useExperiment(
        experiments.wacAISearchSummary
    );
    const isAISearchSummaryEnabled =
        wacAISearchSummaryExperiment &&
        wacAISearchSummaryExperiment.type === "enrolled" &&
        wacAISearchSummaryExperiment.value;

    return (
        <nav
            css={css({
                display: "flex",
                flex: 1,
                justifyContent: "center",
                height: "100%",
                paddingInlineStart: isTemplatesTabInHeader ? "18px" : "24px",
                paddingInlineEnd: "60px",

                // The max width of our main content does not correspond to an ADS breakpoint,
                // but we need to account for the in-betweens with the blue Atlassian logo
                [`@media screen and (min-width: 1306px) and (max-width: 1325.99px)`]:
                    {
                        marginInlineStart: token("space.negative.100"),
                        paddingInlineStart: 8
                    },
                [`@media screen and (min-width: 1326px)`]: {
                    marginInlineStart: token("space.negative.100"),
                    paddingInlineStart: 0
                }
            })}
        >
            <div
                className="productpage-subnav-bar"
                css={[
                    productpageSubnavBar,
                    isTemplatesTabInHeader && wideProductPageSubnavBar
                ]}
            >
                <div
                    className="productpage-subnav-logo-and-links-container"
                    css={productpageSubnavLogoAndLinksContainer}
                    style={{
                        alignItems: isOpenSearch ? "center" : "flex-end",
                        height: "100%"
                    }}
                >
                    <SkipToContent />
                    <div
                        className="productpage-subnav-product-logo"
                        css={productpageSubnavProductLogo}
                        style={{
                            alignSelf: "center",
                            paddingInlineEnd: token("space.200")
                        }}
                    >
                        <a
                            className="navigation-bar-logo-link"
                            css={[
                                aNavBarLogoLink,
                                isTemplatesTabInHeader && smallerJsmLogoStyle
                            ]}
                            href={productLogoUrl}
                            aria-label={productLogoAriaLabel}
                            data-event="clicked"
                            data-event-component="linkButton"
                            data-event-container="productPageNav"
                            data-product-key={productKey ?? "jira"}
                        >
                            {productLogo}
                        </a>
                    </div>
                    {isOpenSearch ? (
                        <SearchBar
                            isOpenSearch={isOpenSearch}
                            setIsOpenSearch={setIsOpenSearch}
                            parentType="ProductNav"
                            css={productpageSearchBar}
                            aiEnabled={isAISearchSummaryEnabled}
                        />
                    ) : (
                        <GlobalNavLinksContainer
                            globalLinksProps={productPageNavLinks}
                            globalLinksRef={linksRef}
                            showMore={shouldShowMoreItems}
                            setShowMore={showMoreItems}
                            maxLinks={maxItems}
                        />
                    )}
                </div>
                {!isOpenSearch &&
                    isShowSearchBarAndProfileContainer &&
                    !shouldShowMoreItems && (
                        <SearchAndProfile
                            isShowTryNowLink={isShowTryNowLink}
                            // @ts-ignore
                            ctaButtonColor={tryNowCTAButtonBackgroundColor}
                            productKey={productKey}
                            isShowSearchButton={isShowSearchButton}
                            setIsOpenSearch={setIsOpenSearch}
                            navigationTrynowCtaText={navigationTrynowCtaText}
                            navigationTrynowCtaUrl={navigationTrynowCtaUrl}
                            navigationTrynowCtaOnClick={
                                navigationTrynowCtaOnClick
                            }
                            navigationTrynowCtaButtonVariant={
                                navigationTrynowCtaButtonVariant
                            }
                            searchAndProfileRef={searchAndProfileRef}
                            positionTryNowCtaOnRight={positionTryNowCtaOnRight}
                            isShowAppSwitcher={isShowAppSwitcher}
                            navType={"productNav"}
                            aiEnabled={isAISearchSummaryEnabled}
                        />
                    )}
            </div>
        </nav>
    );
};

export const ProductPageSubNav = ({
    productLogo,
    productLogoUrl,
    productLogoAriaLabel,
    productKey,
    productPageNavLinks,
    isShowSearchBarAndProfileContainer = true,
    isShowTryNowLink = true,
    tryNowCTAButtonBackgroundColor,
    isShowSearchButton = true,
    navigationTrynowCtaText,
    navigationTrynowCtaUrl = "/try",
    navigationTrynowCtaOnClick,
    navigationTrynowCtaButtonVariant = "primary",
    mobileLogo,
    mobileLinksSections,
    isIncludeMobileCTA = true,
    mobileCtaText,
    mobileCtaUrl = "/software/jira/free",
    mobileCtaOnClick,
    isIncludeMobileAtlassianLogo = true,
    additionalMenuPageObjectsArray = [],
    positionTryNowCtaOnRight = false,
    isShowAppSwitcher = true,
    isShowTabletDropdown = true,
    isTemplatesTabInHeader
}: ProductPageSubNavProps) => {
    const intl = useIntl();
    const [isOpenSearch, setIsOpenSearch] = React.useState(false);

    const defaultCtaText = intl.formatMessage({
        id: "productpagesubnav.try-now-button-label",
        defaultMessage: "Get it free"
    });

    return (
        <GlobalNavProvider
            globalNavItemKeys={productPageNavLinks.map(
                (link) => link.globalLinksKey
            )}
        >
            <header
                css={mobileWrapperCss}
                className="product-page-nav-container"
            >
                <div
                    className="product-page-desktop-nav"
                    css={productPageSubNavStyle}
                >
                    <div css={atlasIconContainerStyle}>
                        <a
                            css={atlasIconStyle}
                            href={useLocalizedHref("/")}
                            aria-label={intl.formatMessage({
                                id: "productPageNav.logo-label",
                                defaultMessage: "Atlassian logo"
                            })}
                            data-event="clicked"
                            data-event-component="linkButton"
                            data-event-container="productPageSubNav"
                            data-product-key="home"
                        >
                            <AtlassianIcon size="medium" appearance="inverse" />
                        </a>
                    </div>
                    <ProductPageSubNavLinks
                        productLogo={productLogo}
                        productLogoUrl={productLogoUrl}
                        productLogoAriaLabel={productLogoAriaLabel}
                        productKey={productKey}
                        productPageNavLinks={productPageNavLinks}
                        isShowSearchBarAndProfileContainer={
                            isShowSearchBarAndProfileContainer
                        }
                        isShowTryNowLink={isShowTryNowLink}
                        tryNowCTAButtonBackgroundColor={
                            tryNowCTAButtonBackgroundColor
                        }
                        isShowSearchButton={isShowSearchButton}
                        isOpenSearch={isOpenSearch}
                        setIsOpenSearch={setIsOpenSearch}
                        navigationTrynowCtaText={
                            navigationTrynowCtaText ?? defaultCtaText
                        }
                        navigationTrynowCtaUrl={navigationTrynowCtaUrl}
                        navigationTrynowCtaOnClick={navigationTrynowCtaOnClick}
                        navigationTrynowCtaButtonVariant={
                            navigationTrynowCtaButtonVariant
                        }
                        positionTryNowCtaOnRight={positionTryNowCtaOnRight}
                        isShowAppSwitcher={isShowAppSwitcher}
                        isTemplatesTabInHeader={isTemplatesTabInHeader}
                    />
                </div>
                <div
                    className="product-page-mobile-nav"
                    css={productPageMobileNav}
                >
                    <MobileProductPageNav
                        productKey={productKey as ProductKeyTypes}
                        mobileLogo={mobileLogo}
                        mobileLogoAriaLabel={productLogoAriaLabel}
                        mobileLogoHref={productLogoUrl}
                        mobileLogoDataEventContainer={"productPageNavLogo"}
                        mobileLinksSections={mobileLinksSections}
                        isIncludeCTA={isIncludeMobileCTA}
                        ctaText={mobileCtaText ?? defaultCtaText}
                        ctaUrl={mobileCtaUrl}
                        ctaOnClick={mobileCtaOnClick}
                        isIncludeAtlassianLogo={isIncludeMobileAtlassianLogo}
                        isShowTryNowLink={isShowTryNowLink}
                        additionalMenuPageObjectsArray={
                            additionalMenuPageObjectsArray
                        }
                        isShowSearchButton={isShowSearchButton}
                        isShowAppSwitcher={isShowAppSwitcher}
                        isShowTabletDropdown={isShowTabletDropdown}
                        navType={"productNav"}
                    />
                </div>
            </header>
        </GlobalNavProvider>
    );
};
